import { Fragment } from 'react';
import styled from 'styled-components';
import { Content, Stack, Strong } from '@kinesis/bungle';
import { DisclosureGpcSummaryPage, GpcSummaryValues } from '@/types';
import { renderUnit, renderValue } from '@/data/units';
import { renderGpcSector, renderGpcSummaryClass } from '@/data/gpc';

// Six columns: the sector/class label + two column groups — by scope
// (S1/S2/S3) and by reporting level (BASIC/BASIC+).
const COLUMNS = 'minmax(0, 1fr) auto auto auto auto auto';

// Bespoke table chrome for this page. GPC Summary and GPC Detail are
// independent table variants, each owning its own grid — deliberately NOT a
// shared component (their shapes diverge: a two-level grouped header here, a
// three-level sector→line→leaf form there). Styling matches bungle's nursery
// `Table`: 1px gridlines (the grid's `gap` showing through a border-coloured
// backing) inside a 1px frame, a `#fafafa` bold header band (`$heading`), 12px
// cell padding and a 32px row height. The nursery `Table` itself is
// column-config and can't express the spanned header / heading rows, so the
// grid is hand-rolled (same approach as the legacy `gpc-summary-table`).
const Table = styled<any>('div')(
  ({ theme }: any) => ({
    display: 'grid',
    gap: 1,
    backgroundColor: theme.borderColor,
    border: `1px solid ${theme.borderColor}`,
    gridAutoRows: 32,
    fontSize: theme.fontSize.medium,
    width: '100%',
  }),
  ({ $columns }: any) => ({ gridTemplateColumns: $columns }),
);

// One grid cell. `$heading` is the bold `#fafafa` band (column header, sector
// heading, total row); `$align` right-/centre-aligns a column; `$indent`
// left-pads a nested label (a class under its sector); `$span` widens a cell
// across N columns (the grouped header + the sector/total rows). Single line,
// ellipsised; the 32px line-height vertically centres against the row.
const Cell = styled<any>('div')(
  ({ $heading, $align, $indent, $span }: any) => ({
    backgroundColor: $heading ? '#fafafa' : 'white',
    fontWeight: $heading ? 'bold' : 'normal',
    textAlign: $align ?? 'left',
    lineHeight: '32px',
    padding: '0 12px',
    paddingLeft: $indent ? 28 : undefined,
    gridColumn: $span ? `span ${$span}` : undefined,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
);

// Renders the GPC Summary page — the fixed full-form table: all eight summary
// classes under all six sector headings, populated or blank, with a two-level
// column header (by scope · by reporting level). Every value is server-
// resolved in the page `unit` (the FE never picks a unit); a blank cell
// renders "—", never 0 — a GPC form is a completeness statement, so empty rows
// are shown, not dropped. BASIC = sectors I+II+III; BASIC+ = all six. The
// `total` row carries the server's column totals (no separate headline box).
// Store-less per the component/page split; the page owns the RTK call.
const DisclosureGpcSummary = (props: DisclosureGpcSummaryProps) => {
  const { pageData } = props;
  const { unit, sectors, total } = pageData;
  // Absent value → "—" (blank, not a fabricated 0); a present 0 renders "0".
  const cell = (value: number | undefined) =>
    value === undefined ? '—' : renderValue(value, unit);
  const valueCells = (values: GpcSummaryValues, heading?: boolean) => (
    <>
      <Cell $heading={heading} $align='right'>
        {cell(values.scope1)}
      </Cell>
      <Cell $heading={heading} $align='right'>
        {cell(values.scope2)}
      </Cell>
      <Cell $heading={heading} $align='right'>
        {cell(values.scope3)}
      </Cell>
      <Cell $heading={heading} $align='right'>
        {cell(values.basic)}
      </Cell>
      <Cell $heading={heading} $align='right'>
        {cell(values.basicPlus)}
      </Cell>
    </>
  );
  return (
    <Stack space='medium'>
      <Strong>Summary of GHG emissions ({renderUnit(unit)})</Strong>
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        <Table $columns={COLUMNS}>
          {/* Group header (row 1 of the two-level column header). */}
          <Cell $heading aria-hidden='true' />
          <Cell $heading $align='center' $span={3}>
            By scope
          </Cell>
          <Cell $heading $align='center' $span={2}>
            By reporting level
          </Cell>
          {/* Column header (row 2). */}
          <Cell $heading>Sector / class</Cell>
          <Cell $heading $align='right'>
            S1
          </Cell>
          <Cell $heading $align='right'>
            S2
          </Cell>
          <Cell $heading $align='right'>
            S3
          </Cell>
          <Cell $heading $align='right'>
            BASIC
          </Cell>
          <Cell $heading $align='right'>
            BASIC+
          </Cell>
          {sectors.map((sector) => (
            <Fragment key={sector.sector}>
              <Cell $heading $span={6}>
                {renderGpcSector(sector.sector)}
              </Cell>
              {sector.classes.map((row) => (
                <Fragment key={row.class}>
                  <Cell $indent>{renderGpcSummaryClass(row.class)}</Cell>
                  {valueCells(row)}
                </Fragment>
              ))}
            </Fragment>
          ))}
          <Cell $heading>Total</Cell>
          {valueCells(total, true)}
        </Table>
      </Content>
    </Stack>
  );
};

export type DisclosureGpcSummaryProps = {
  pageData: DisclosureGpcSummaryPage;
};

export { DisclosureGpcSummary };
